import { Scribbles } from "@somethings-ui/core/assets";
import { ChevronLeft } from "lucide-react";
import { useCallback, useState } from "react";
import type { UseActionAnalyticsResult } from "../use-action-analytics";
import { AiChatComposer } from "./ai-chat-composer";
import { AiChatThread } from "./ai-chat-thread";
import { useAiChat } from "./use-ai-chat";
import type { AiChatPrompt } from "./ai-chat-prompts";

interface AiChatPanelProps {
  mentorshipId: string | null;
  teenName: string;
  onBack: () => void;
  active: boolean;
  analytics: UseActionAnalyticsResult;
}

export function AiChatPanel({
  mentorshipId,
  teenName,
  onBack,
  active,
  analytics,
}: AiChatPanelProps) {
  const chat = useAiChat(mentorshipId, active);
  const [draft, setDraft] = useState("");

  const disabled = mentorshipId === null || chat.isStreaming;

  const handleSubmit = useCallback(() => {
    const trimmed = draft.trim();
    if (trimmed.length === 0 || disabled) return;
    analytics.trackAiQuerySubmit(trimmed.length);
    chat.send(trimmed);
    setDraft("");
  }, [analytics, chat, disabled, draft]);

  const handleSelectPrompt = useCallback(
    (prompt: AiChatPrompt, label: string) => {
      if (disabled) return;
      analytics.trackAiChipClick(prompt.id);
      chat.send(label);
    },
    [analytics, chat, disabled],
  );

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex shrink-0 items-center justify-between px-4 pt-3">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-2 font-body text-body-3 text-text-tertiary transition-colors hover:text-text-secondary focus-visible:outline-none"
        >
          <ChevronLeft className="size-4" aria-hidden />
          Context
        </button>
        <span
          className="flex size-8 items-center justify-center rounded-full border border-surfaces-z5 bg-white"
          aria-hidden
        >
          <img
            src={Scribbles.orangeLoops}
            alt=""
            className="size-6 object-contain"
          />
        </span>
      </div>

      <AiChatThread
        messages={chat.messages}
        pendingQuestion={chat.pendingQuestion}
        streamingAnswer={chat.streamingAnswer}
        isStreaming={chat.isStreaming}
        isLoadingHistory={chat.isLoadingHistory}
        error={chat.error}
        isEmpty={chat.isEmpty}
        teenName={teenName}
        onSelectPrompt={handleSelectPrompt}
        onRetry={chat.retry}
      />

      <AiChatComposer
        value={draft}
        onChange={setDraft}
        onSubmit={handleSubmit}
        teenName={teenName}
        loading={chat.isStreaming}
        disabled={mentorshipId === null}
      />
    </div>
  );
}
